import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTenant } from '@/contexts/TenantContext';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

const f = "'IBM Plex Mono', monospace";
const HOVER_BLUE = '#0057FF';

export default function AdminLogin() {
  const { username: urlUsername } = useParams<{ username: string }>();
  const { username: tenantUsername } = useTenant();
  const effectiveUsername = tenantUsername || urlUsername;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [siteName, setSiteName] = useState<string | null>(null);
  const [googleHover, setGoogleHover] = useState(false);
  const [appleHover, setAppleHover] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();

  // Fetch site name for left column
  useEffect(() => {
    if (!effectiveUsername) return;
    (async () => {
      const { data } = await supabase
        .from('public_profiles')
        .select('site_name')
        .eq('username', effectiveUsername)
        .maybeSingle();
      if (data?.site_name) setSiteName(data.site_name);
    })();
  }, [effectiveUsername]);

  // Redirect if already logged in
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        window.location.href = '/admin';
      }
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const { error } = await signIn(email, password);
    if (error) {
      setLoading(false);
      setError('e-mail ou senha não conferem.');
      return;
    }

    setLoading(false);
    if (effectiveUsername) {
      window.location.href = `https://${effectiveUsername}.criaaa.com/admin`;
    } else {
      navigate('/admin', { replace: true });
    }
  };

  const handleGoogleLogin = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: 'https://criaaa.com/auth/callback',
      },
    });
    if (error) console.error(error);
  };

  const handleAppleLogin = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: {
        redirectTo: 'https://criaaa.com/auth/callback',
      },
    });
    if (error) console.error(error);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError('');
    setForgotLoading(true);
    let redirectTo = 'https://www.criaaa.com/reset-password';
    if (tenantUsername) {
      redirectTo = `https://${tenantUsername}.criaaa.com/admin/reset-password`;
    } else if (effectiveUsername) {
      redirectTo = `https://www.criaaa.com/${effectiveUsername}/admin/reset-password`;
    }
    const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
      redirectTo,
    });
    setForgotLoading(false);
    if (error) {
      setForgotError('erro ao enviar email. tente novamente.');
    } else {
      setForgotSent(true);
    }
  };

  const inputStyle: React.CSSProperties = {
    fontFamily: f,
    fontSize: 'var(--type-body)',
    width: '100%',
    background: 'transparent',
    border: '0.5px solid var(--lp-border2)',
    borderRadius: 0,
    padding: '0.85rem 1.25rem',
    color: 'var(--lp-ink)',
    outline: 'none',
    boxSizing: 'border-box' as const,
  };

  const isFormValid = email.trim() && password.length > 0;

  return (
    <div style={{ background: 'var(--lp-bg)', minHeight: '100vh', fontFamily: f, color: 'var(--lp-ink)' }}>
      {/* Fixed header with logo */}
      <header style={{
        position: 'fixed', top: 0, left: 0, right: 0,
        padding: '1.25rem 2.5rem',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        zIndex: 100,
      }}>
        <a href="https://criaaa.com" style={{
          fontFamily: f,
          fontSize: '1rem', fontWeight: 400,
          letterSpacing: '0.22em', textTransform: 'uppercase',
          textDecoration: 'none', color: 'var(--lp-ink)',
        }}>
          CRIAAA
        </a>
      </header>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          minHeight: '100vh',
          paddingTop: '5rem',
        }}
        className="admin-login-grid"
      >
        {/* Left column — dark, portfolio identity */}
        <div
          className="admin-login-left"
          style={{
            background: 'var(--lp-ink)',
            color: 'var(--lp-bg)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'flex-start',
            padding: '9rem 4rem 4rem',
          }}
        >
          <p style={{
            fontSize: 'clamp(2rem, 4vw, 3.5rem)',
            fontWeight: 300, lineHeight: 1.2,
            marginBottom: '0.5rem', margin: 0,
          }}>
            {siteName || effectiveUsername || 'criaaa'}
          </p>
          {effectiveUsername && (
            <p style={{
              fontSize: 'var(--type-label)',
              letterSpacing: '0.14em', textTransform: 'uppercase',
              color: 'rgba(247,246,242,0.4)',
              marginTop: '0.5rem',
            }}>
              criaaa.com/{effectiveUsername}
            </p>
          )}
        </div>

        {/* Right column — form */}
        <div
          style={{
            background: 'var(--lp-bg)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: '4rem',
          }}
        >
          <div style={{ maxWidth: 480, width: '100%' }}>
            {forgotMode ? (
              <>
                <h1 style={{ fontSize: 'var(--type-h2)', fontWeight: 300, marginBottom: '2.5rem' }}>
                  Recuperar senha
                </h1>
                {forgotSent ? (
                  <div>
                    <p style={{ fontSize: 'var(--type-body)', color: 'var(--lp-muted)', lineHeight: 1.6 }}>
                      se o email existir em nossa base, você receberá um link de recuperação em instantes.
                    </p>
                    <button
                      type="button"
                      onClick={() => { setForgotMode(false); setForgotSent(false); setForgotEmail(''); }}
                      style={{
                        fontFamily: f, fontSize: 'var(--type-caption)',
                        color: 'var(--lp-muted)', background: 'none', border: 'none',
                        cursor: 'pointer', marginTop: '1.5rem', padding: 0,
                        textDecoration: 'underline', textUnderlineOffset: 3,
                      }}
                    >
                      ← voltar ao login
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPassword} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <p style={{ fontSize: 'var(--type-body)', color: 'var(--lp-muted)', lineHeight: 1.5, marginBottom: '1rem' }}>
                      digite seu email para receber o link de redefinição.
                    </p>
                    <input
                      type="email"
                      placeholder="seu@email.com"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                      style={inputStyle}
                    />
                    {forgotError && <p style={{ fontSize: 'var(--type-caption)', color: '#C0392B' }}>{forgotError}</p>}
                    <button
                      type="submit"
                      disabled={forgotLoading}
                      style={{
                        fontFamily: f, fontSize: 'var(--type-label)',
                        textTransform: 'uppercase', letterSpacing: '0.14em',
                        width: '100%', background: 'var(--lp-ink)', color: 'var(--lp-bg)',
                        border: 'none', borderRadius: 0, padding: '0.9rem',
                        cursor: forgotLoading ? 'wait' : 'pointer',
                        marginTop: '1.5rem',
                      }}
                    >
                      {forgotLoading ? 'ENVIANDO...' : 'ENVIAR LINK'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setForgotMode(false); setForgotError(''); }}
                      style={{
                        fontFamily: f, fontSize: 'var(--type-caption)',
                        color: 'var(--lp-muted)', background: 'none', border: 'none',
                        cursor: 'pointer', padding: 0, marginTop: '1rem',
                        textDecoration: 'underline', textUnderlineOffset: 3,
                      }}
                    >
                      ← voltar ao login
                    </button>
                  </form>
                )}
              </>
            ) : (
              <>
                <h1 style={{ fontSize: 'var(--type-h2)', fontWeight: 300, marginBottom: '2.5rem' }}>
                  Entrar
                </h1>

                {/* Social buttons */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <button
                    type="button"
                    onClick={handleGoogleLogin}
                    onMouseEnter={() => setGoogleHover(true)}
                    onMouseLeave={() => setGoogleHover(false)}
                    style={{
                      width: '100%', padding: '0.85rem', fontFamily: f,
                      fontSize: 'var(--type-label)', letterSpacing: '0.14em',
                      textTransform: 'uppercase', background: 'transparent',
                      border: `0.5px solid ${googleHover ? HOVER_BLUE : 'var(--lp-border2)'}`,
                      borderRadius: 0, color: googleHover ? HOVER_BLUE : 'var(--lp-ink)',
                      cursor: 'pointer', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', gap: '0.75rem',
                      transition: 'border-color 0.2s ease, color 0.2s ease',
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    CONTINUAR COM GOOGLE
                  </button>
                  <button
                    type="button"
                    onClick={handleAppleLogin}
                    onMouseEnter={() => setAppleHover(true)}
                    onMouseLeave={() => setAppleHover(false)}
                    style={{
                      width: '100%', padding: '0.85rem', fontFamily: f,
                      fontSize: 'var(--type-label)', letterSpacing: '0.14em',
                      textTransform: 'uppercase', background: 'transparent',
                      border: `0.5px solid ${appleHover ? HOVER_BLUE : 'var(--lp-border2)'}`,
                      borderRadius: 0, color: appleHover ? HOVER_BLUE : 'var(--lp-ink)',
                      cursor: 'pointer', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', gap: '0.75rem',
                      transition: 'border-color 0.2s ease, color 0.2s ease',
                    }}
                  >
                    <svg width="14" height="16" viewBox="0 0 814 1000" fill="currentColor">
                      <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 790.7 0 663 0 541.8c0-207.5 135.4-317.3 269-317.3 70.1 0 128.4 46.4 172.5 46.4 42.8 0 109.6-49 192.5-49 30.8 0 108.2 2.6 168.6 74.8zm-234.5-185.1c31.4-37.9 53.9-90.5 53.9-143.1 0-7.7-.6-15.4-1.9-21.7-50.6 1.9-110.4 33.7-146.1 75.8-28.2 32.1-54.5 84.7-54.5 138 0 8.3 1.3 16.6 1.9 19.2 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 133.1-69.5z"/>
                    </svg>
                    CONTINUAR COM APPLE
                  </button>
                </div>

                {/* Divider */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', margin: '1.5rem 0' }}>
                  <div style={{ flex: 1, borderTop: '0.5px solid var(--lp-border)' }} />
                  <span style={{ fontSize: 'var(--type-caption)', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--lp-muted)' }}>OU</span>
                  <div style={{ flex: 1, borderTop: '0.5px solid var(--lp-border)' }} />
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <input
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    style={inputStyle}
                  />
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="senha"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      style={{ ...inputStyle, paddingRight: '3.5rem' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute', right: '1rem', top: '50%',
                        transform: 'translateY(-50%)', background: 'none',
                        border: 'none', cursor: 'pointer', fontFamily: f,
                        fontSize: 'var(--type-caption)', color: 'var(--lp-muted)',
                        textTransform: 'uppercase', letterSpacing: '0.08em', padding: 0,
                      }}
                    >
                      {showPassword ? 'OCULTAR' : 'VER'}
                    </button>
                  </div>

                  {error && (
                    <p style={{
                      fontSize: 'var(--type-caption)', color: '#C0392B',
                      letterSpacing: '0.06em', marginTop: '0.75rem',
                      textTransform: 'lowercase',
                    }}>
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={!isFormValid || loading}
                    style={{
                      fontFamily: f, fontSize: 'var(--type-label)',
                      textTransform: 'uppercase', letterSpacing: '0.14em',
                      width: '100%', background: 'var(--lp-ink)', color: 'var(--lp-bg)',
                      border: 'none', borderRadius: 0, padding: '0.9rem',
                      cursor: isFormValid && !loading ? 'pointer' : 'not-allowed',
                      opacity: isFormValid && !loading ? 1 : 0.4,
                      marginTop: '1.5rem', transition: 'opacity 0.2s ease',
                    }}
                  >
                    {loading ? 'ENTRANDO...' : 'ENTRAR NO CMS'}
                  </button>
                </form>

                <p style={{ fontSize: 'var(--type-caption)', color: 'var(--lp-muted)', marginTop: '1.5rem' }}>
                  <button
                    type="button"
                    onClick={() => setForgotMode(true)}
                    style={{
                      fontFamily: f, fontSize: 'var(--type-caption)',
                      color: 'var(--lp-muted)', background: 'none', border: 'none',
                      cursor: 'pointer', padding: 0,
                      textDecoration: 'underline', textUnderlineOffset: 3,
                    }}
                  >
                    esqueceu a senha?
                  </button>
                </p>

                <a href="https://criaaa.com" style={{
                  fontSize: 'var(--type-caption)',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: 'var(--lp-muted)',
                  textDecoration: 'none',
                  display: 'block',
                  marginTop: '2rem',
                  transition: 'color 0.2s ease',
                  fontFamily: f,
                }}
                onMouseEnter={e => e.currentTarget.style.color = '#0057FF'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--lp-muted)'}
                >
                  ← criaaa.com
                </a>
              </>
            )}
          </div>
        </div>
      </div>

      <style>{`
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus {
          -webkit-box-shadow: 0 0 0px 1000px var(--lp-bg) inset !important;
          -webkit-text-fill-color: var(--lp-ink) !important;
          transition: background-color 5000s ease-in-out 0s;
        }
        @media (max-width: 768px) {
          .admin-login-grid {
            grid-template-columns: 1fr !important;
          }
          .admin-login-left {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
